import { Flex, Skeleton, Tabs, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useIdeaInfoTabsConfig } from '../../../hooks/ideaPage/useIdeaQueries';
import { evaluateDisplayIf } from '../../ideaNiv/right-bar/evidence-card/CardInfoTextItem';
import { resolveFieldWithFallback } from '../../ideaNiv/right-bar/evidence-card/infotab-utils';
import { formatConfigFieldValue } from '../../../utils/evidence/formatConfigFieldValue';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import type { EnrichedEvidence } from './types';

export interface DiscoveryEvidenceMetadataProps {
  evidence: EnrichedEvidence;
  variant?: 'full' | 'compact';
}

/**
 * Same rules as CardInfo / Evidence modal: container evidences OR child match displayIf;
 * non-container uses the evidence row only.
 */
function displayConditionForItem(item: Record<string, unknown>, displayIf: any): boolean {
  if (!displayIf) return true;
  if (item?.isContainer && (item?.childEvidences as any[])?.length > 0) {
    return (item.childEvidences as any[]).some((child) =>
      evaluateDisplayIf(child, displayIf)
    );
  }
  return evaluateDisplayIf(item, displayIf);
}

/** Dynamic info tabs only — same rules as CardInfo `filteredTabs` (details is excluded there). */
function dynamicTabVisibleForItem(
  item: Record<string, unknown>,
  tabKey: string,
  tabsConfig: Record<string, any>
): boolean {
  const tab = tabsConfig[tabKey];
  if (!tab) return false;
  return displayConditionForItem(item, tab.displayIf);
}

function fieldRowVisibleForItem(item: Record<string, unknown>, property: any): boolean {
  return displayConditionForItem(item, property?.displayIf);
}

/** infoTabs API may return `{ ideaInfoTabsConfig: { details, ... } }` or tabs at root. */
function resolveTabsConfig(raw: unknown): Record<string, any> {
  if (!raw || typeof raw !== 'object') return {};
  const d = raw as Record<string, any>;
  if (d.ideaInfoTabsConfig && typeof d.ideaInfoTabsConfig === 'object') {
    return d.ideaInfoTabsConfig as Record<string, any>;
  }
  if (d.details !== undefined && typeof d.details === 'object') {
    return d as Record<string, any>;
  }
  return {};
}

function DetailsFields({
  item,
  detailsTabConfig,
  isLoadingInfoTabs,
  variant,
}: {
  item: Record<string, unknown>;
  detailsTabConfig: { properties?: any[] } | undefined;
  isLoadingInfoTabs: boolean;
  variant: 'full' | 'compact';
}) {
  const hasConfigurableDetailsFields =
    detailsTabConfig?.properties && detailsTabConfig.properties.length > 0;

  return (
    <Flex direction="column" gap={6}>
      <Flex h={28} align="center" wrap="wrap">
        <Text fz={12} w={120} c="dimmed">
          Product area
        </Text>
        <Text fz={12}>
          {(item.componentObj as { name?: string } | undefined)?.name || 'Unassigned'}
        </Text>
      </Flex>
      <Flex h={28} align="center" wrap="wrap">
        <Text fz={12} w={120} c="dimmed">
          Owner
        </Text>
        <Text fz={12}>
          {capitalizeFirstLetter(
            String(
              (item.owner as { name?: string } | undefined)?.name ||
                (item.ownerName as string) ||
                'Unassigned'
            )
          )}
        </Text>
      </Flex>

      {isLoadingInfoTabs && (
        <>
          <Skeleton h={28} w="100%" />
          <Skeleton h={28} w="100%" />
        </>
      )}

      {!isLoadingInfoTabs &&
        hasConfigurableDetailsFields &&
        detailsTabConfig!.properties!.map((property: any) => {
          if (!fieldRowVisibleForItem(item, property)) return null;
          const raw = resolveFieldWithFallback(item, property.fieldPath);
          const formatted = formatConfigFieldValue(raw, property);
          if (formatted === null) return null;
          const display =
            variant === 'compact' && formatted.length > 120
              ? `${formatted.slice(0, 120)}…`
              : formatted;
          return (
            <Flex key={property.fieldPath} h={28} align="center" wrap="wrap">
              <Text fz={12} w={120} c="dimmed">
                {property.label}
              </Text>
              <Text fz={12}>{display}</Text>
            </Flex>
          );
        })}
    </Flex>
  );
}

function InfoTabPanel({
  item,
  tab,
}: {
  item: Record<string, unknown>;
  tab: { label?: string; properties?: any[] };
}) {
  return (
    <Flex direction="column" gap={6}>
      {tab.properties?.map((property: any) => {
        if (!fieldRowVisibleForItem(item, property)) return null;
        const raw = resolveFieldWithFallback(item, property.fieldPath);
        const formatted = formatConfigFieldValue(raw, property);
        if (formatted === null) return null;
        return (
          <Flex key={property.fieldPath} h={28} align="center" wrap="wrap">
            <Text fz={12} w={120} c="dimmed">
              {property.label}
            </Text>
            <Text fz={12}>{formatted}</Text>
          </Flex>
        );
      })}
    </Flex>
  );
}

export function DiscoveryEvidenceMetadata({
  evidence,
  variant = 'full',
}: DiscoveryEvidenceMetadataProps) {
  const item = evidence as Record<string, unknown>;
  const { data: ideaInfoTabsConfigData, isLoading: isLoadingInfoTabs } =
    useIdeaInfoTabsConfig();

  const tabsConfig = useMemo(
    () => resolveTabsConfig(ideaInfoTabsConfigData),
    [ideaInfoTabsConfigData]
  );

  const detailsTabConfig = tabsConfig['details'];

  const paddingY = variant === 'compact' ? 8 : 12;

  const { dynamicTabKeys, defaultTab } = useMemo(() => {
    const keys = Object.keys(tabsConfig).filter(
      (k) => k !== 'details' && dynamicTabVisibleForItem(item, k, tabsConfig)
    );
    return {
      dynamicTabKeys: keys,
      defaultTab: 'details',
    };
  }, [item, tabsConfig]);

  if (variant === 'compact') {
    return (
      <Flex direction="column" gap={6} py={paddingY}>
        <DetailsFields
          item={item}
          detailsTabConfig={detailsTabConfig}
          isLoadingInfoTabs={isLoadingInfoTabs}
          variant="compact"
        />
      </Flex>
    );
  }

  return (
    <Tabs defaultValue={defaultTab} variant="default" mt="xs">
      <Tabs.List>
        <Tabs.Tab value="details">Details</Tabs.Tab>
        {dynamicTabKeys.map((tabKey) => {
          const tab = tabsConfig[tabKey];
          if (!tab) return null;
          return (
            <Tabs.Tab key={tabKey} value={tabKey}>
              {tab.label}
            </Tabs.Tab>
          );
        })}
      </Tabs.List>

      <Tabs.Panel value="details" pt="sm" pb="xs">
        <DetailsFields
          item={item}
          detailsTabConfig={detailsTabConfig}
          isLoadingInfoTabs={isLoadingInfoTabs}
          variant="full"
        />
      </Tabs.Panel>

      {dynamicTabKeys.map((tabKey) => {
        const tab = tabsConfig[tabKey];
        if (!tab) return null;
        return (
          <Tabs.Panel key={tabKey} value={tabKey} pt="sm" pb="xs">
            <InfoTabPanel item={item} tab={tab} />
          </Tabs.Panel>
        );
      })}
    </Tabs>
  );
}
